/**
 * 動画 1 本を名指しで読み取る導線（一覧の操作メニューと、詳細の見出しの右）。
 *
 * ## 押せるかどうかを 1 か所で決める
 *
 * 一覧と詳細で判定を別に持つと、片方だけが「押せるのに断られる」導線になる。
 * `processActionFor` だけが、押せるか・押したら何をするか・押せない理由を決める。
 *
 * - まだ処理していない / 失敗 … 処理する
 * - 処理済み … もう一度処理する（`reprocess: true`。前回の結果を置き換える）
 * - mp4 が無く HLS（m3u8）だけがある … m3u8 から処理する（ADR 0003）
 * - 処理に渡す URL が 1 つも無い / 原本が 200 MB を超えた … **押せない**。理由を言葉で出す
 *
 * ## 押した瞬間には始めない
 *
 * 読み取りは費用の出る実行なので、1 度確かめてから始める（一括の側と同じ）。
 * 起動の鍵は確かめる画面を開くたびに 1 つ作る。押し直しのたびに作り直すと、
 * 同じ動画を二重に起動できてしまう。
 */

import { useState } from "react";
import { useMutation } from "urql";
import {
  Button,
  EnqueueVideoMetaJobDocument,
  type JobProgressFieldsFragment,
} from "visumo-data-agent-web-shared";

/** 判定に要るものだけ。一覧の行と詳細の両方が満たす。 */
export interface ProcessableVideo {
  readonly processState: string;
  readonly videoUrl?: string | null;
  readonly hlsUrl?: string | null;
  readonly skipReason?: string | null;
}

export type ProcessAction =
  | {
      readonly kind: "available";
      /** 読み取り済みの動画を読み直す */
      readonly reprocess: boolean;
      /** mp4 が無く、m3u8 を結合して処理する */
      readonly fromHls: boolean;
    }
  | { readonly kind: "unavailable"; readonly reason: string };

export function processActionFor(video: ProcessableVideo): ProcessAction {
  if (video.processState === "RUNNING") {
    return {
      kind: "unavailable",
      reason: "処理中です。終わると状態が変わります。",
    };
  }
  const hasMp4 = (video.videoUrl ?? null) !== null;
  const hasHls = (video.hlsUrl ?? null) !== null;
  if (!hasMp4 && !hasHls) {
    return {
      kind: "unavailable",
      reason:
        "動画ファイルの URL（mp4 も m3u8 も）が取り込まれていないので、処理できません。",
    };
  }
  if (video.processState === "SKIPPED" && video.skipReason === "TOO_LARGE") {
    return {
      kind: "unavailable",
      reason: "原本が 200 MB を超えているので、処理できません。",
    };
  }
  return {
    kind: "available",
    reprocess: video.processState === "DONE",
    fromHls: !hasMp4,
  };
}

/**
 * 押す導線の文言。詳細では見出しの右に 1 つだけ置くので、何を処理するかを言う。
 * 読み直しは、m3u8 からでも「もう一度処理する」にする（読み直すことのほうが大事）。
 */
export function processLabel(
  action: Extract<ProcessAction, { kind: "available" }>,
  place: "list" | "detail",
): string {
  if (action.reprocess) return "もう一度処理する";
  if (action.fromHls) return "処理する（m3u8 から）";
  return place === "detail" ? "この動画を処理する" : "処理する";
}

/**
 * 確かめる画面。**ここで押して初めて起動する。**
 *
 * 読み直しでは、前回の結果が置き換わることと、確定した商品は残ることを先に言う。
 */
export function VideoProcessConfirm({
  videoId,
  title,
  action,
  onStarted,
  onCancel,
}: {
  videoId: string;
  /** 読み上げの名前に使う（どの動画を処理するのか） */
  title: string;
  action: Extract<ProcessAction, { kind: "available" }>;
  onStarted: (job: JobProgressFieldsFragment) => void;
  onCancel: () => void;
}) {
  const [result, enqueue] = useMutation(EnqueueVideoMetaJobDocument);
  // 確かめる画面を開いたときに 1 つだけ作る
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const userErrors = result.data?.enqueueVideoMetaJob.userErrors ?? [];

  return (
    <div className="vd-stack" style={{ marginTop: 8 }}>
      <div className="vd-row">
        <span className="vd-stat-note">
          {action.reprocess
            ? "この 1 本をもう一度読み取ります。前回の読み取り結果は置き換わります（確定した商品は残ります）。"
            : action.fromHls
              ? "この 1 本を、m3u8 を結合してから読み取ります。"
              : "この 1 本を読み取ります。"}
          AWS の費用がかかります。
        </span>
        <Button
          variant="primary"
          disabled={result.fetching}
          aria-label={`${title}をこの内容で処理する`}
          onClick={() => {
            void enqueue({
              input: {
                targets: [
                  {
                    videoId,
                    ...(action.reprocess ? { reprocess: true } : {}),
                  },
                ],
                idempotencyKey,
              },
            }).then((outcome) => {
              const started = outcome.data?.enqueueVideoMetaJob.job ?? null;
              if (started === null) return;
              onStarted(started);
            });
          }}
        >
          {result.fetching ? "起動しています" : "この内容で処理する"}
        </Button>
        <Button disabled={result.fetching} onClick={onCancel}>
          やめる
        </Button>
      </div>

      {userErrors.length === 0 ? null : (
        <div className="vd-state vd-state--error" role="alert">
          <span className="vd-state-title">読み取りを始められませんでした</span>
          {userErrors.map((error) => (
            <span key={`${error.code}-${error.message}`}>{error.message}</span>
          ))}
        </div>
      )}
      {result.error === undefined ? null : (
        <div className="vd-state vd-state--error" role="alert">
          <span className="vd-state-title">読み取りを始められませんでした</span>
          <span>{result.error.message}</span>
        </div>
      )}
    </div>
  );
}
